import {
  LayoutDashboard,
  FileText,
  Award,
  Settings,
  BarChart3,
  Megaphone,
  ChevronDown,
  Plus,
  FileQuestion,
  CreditCard,
} from 'lucide-react'

import { Link, NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/utils/cn'
import { useAuth } from '@/contexts/AuthContext'
import { UserRole } from '@/models/user.model'
import { paths } from '@/routes/path'
import logo from '@/assets/images/logo/logo-text.png'

interface NavItem {
  label: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  roles?: UserRole[]
  hasDropdown?: boolean
}

const navigation: NavItem[] = [
  {
    label: 'Dashboard',
    href: paths.app.dashboard.absolute,
    icon: LayoutDashboard,
  },
  {
    label: 'Applications',
    href: paths.app.applications.root.absolute,
    icon: FileText,
    roles: [UserRole.ADMIN],
  },
  {
    label: 'Faculties',
    href: paths.app.faculties.root.absolute,
    icon: FileText,
    roles: [UserRole.ADMIN],
  },
  {
    label: 'Departments',
    href: paths.app.departments.root.absolute,
    icon: FileText,
    roles: [UserRole.ADMIN],
  },
  {
    label: 'Programmes',
    href: paths.app.programmes.root.absolute,
    icon: FileText,
    roles: [UserRole.ADMIN],
  },
  {
    label: 'All Courses',
    href: paths.app.courses.root.absolute,
    icon: Award,
    roles: [UserRole.INSTRUCTOR, UserRole.ADMIN],
  },
  {
    label: 'Courses',
    href: paths.app.courses.learner.absolute,
    icon: FileText,
    roles: [UserRole.LEARNER],
  },
  {
    label: 'Application Payments',
    href: paths.app.payments.absolute,
    icon: CreditCard,
    roles: [UserRole.LEARNER],
  },
  {
    label: 'Assignments',
    href: paths.app.assignments.root.absolute,
    icon: FileText,
  },
  {
    label: 'Certificates',
    href: paths.app.certificates.absolute,
    icon: Award,
    roles: [UserRole.ADMIN],
  },
  {
    label: 'Quizzes',
    href: paths.app.quizzes.root.absolute,
    icon: FileQuestion,
    roles: [UserRole.ADMIN, UserRole.INSTRUCTOR],
  },
  {
    label: 'Role Management',
    href: paths.app.users.root.absolute,
    icon: FileText,
    roles: [UserRole.ADMIN],
  },
  {
    label: 'Student Management',
    href: paths.app.studentManagement.root.absolute,
    icon: FileText,
    roles: [UserRole.INSTRUCTOR, UserRole.ADMIN],
  },
  // {
  //   label: 'Analytics',
  //   href: paths.app.analytics.absolute,
  //   icon: BarChart3,
  //   roles: [UserRole.INSTRUCTOR, UserRole.ADMIN],
  // },
  {
    label: 'Announcements',
    href: paths.app.announcements.root.absolute,
    icon: Megaphone
  },
  // {
  //   label: 'More',
  //   href: paths.app.more.absolute,
  //   icon: Settings,
  //   hasDropdown: true,
  // },
]

interface SidebarProps {
  isOpen?: boolean
  onClose?: () => void
}

export function Sidebar({ isOpen = false, onClose }: SidebarProps = {}) {
  const { user } = useAuth()
  const location = useLocation()

  const filteredNavigation = navigation.filter(
    (item) => !item.roles || (user && item.roles.includes(user.role as any))
  )

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/40 transition-opacity lg:hidden',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        aria-hidden={!isOpen}
        onClick={onClose}
      />
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 -translate-x-full bg-white shadow-lg transition-transform duration-300 ease-in-out lg:static lg:z-auto lg:translate-x-0 lg:shadow-none',
          isOpen ? 'translate-x-0' : 'lg:translate-x-0'
        )}
      >
        <div className="flex h-full flex-col border-r border-gray-200 bg-white">
          {/* Logo Section */}
          <div className="border-b border-gray-200 p-4 sm:p-6">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Link to={paths.app.dashboard.absolute} className="text-2xl font-bold text-[#7b2a40]">
                <img src={logo} alt="KBS Logo" className="h-12 object-contain" />
              </Link>
            </div>
          </div>

          {/* Navigation */}
          <nav
            id="navigation"
            tabIndex={-1}
            className="flex-1 space-y-1 overflow-y-auto p-3 sm:space-y-2 sm:p-4"
          >
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href
              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  onClick={onClose}
                  className={cn(
                    'flex items-center justify-between rounded-lg px-2 py-2 text-xs font-medium transition-colors sm:px-3 sm:text-sm',
                    isActive
                      ? 'border border-red-200 bg-red-50 text-red-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  )}
                >
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <item.icon
                      className={cn(
                        'h-4 w-4 sm:h-5 sm:w-5',
                        isActive ? 'text-red-600' : 'text-gray-500'
                      )}
                    />
                    <span className="truncate">{item.label}</span>
                  </div>
                  {item.hasDropdown && (
                    <ChevronDown className="h-3 w-3 flex-shrink-0 text-gray-400 sm:h-4 sm:w-4" />
                  )}
                </NavLink>
              )
            })}
          </nav>

          {/* Help Center */}
          <div className="border-t border-gray-200 p-3 sm:p-4">
            <div className="rounded-lg bg-primary p-3 text-white sm:p-4">
              <div className="text-xs font-medium sm:text-sm">Our Help Center</div>
              <div className="mb-1 text-sm font-bold sm:mb-2 sm:text-lg">Having any trouble?</div>
              <div className="mb-2 text-xs sm:mb-3 sm:text-sm">Please contact us for more questions.</div>
              <button className="w-full rounded bg-primary-300 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-white hover:text-primary sm:px-4 sm:py-2 sm:text-sm">
                info@kbs.edu.ng
              </button>
            </div>
          </div>

          {/* Create New Course Section - Only for Instructors and Admins */}
          {/* {user && (user.role === UserRole.INSTRUCTOR || user.role === UserRole.ADMIN) && (
            <div className="p-3 sm:p-4">
              <div className="rounded-lg bg-gray-50 p-3 sm:p-4">
                <div className="mb-2 text-xs font-medium text-gray-900 sm:text-sm">Start Creating your next Course</div>
                <button className="flex w-full items-center justify-center rounded bg-primary px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-primary-600 sm:px-4 sm:py-2 sm:text-sm">
                  <Plus className="mr-1 h-3 w-3 sm:mr-2 sm:h-4 sm:w-4" />
                  Create New Course
                </button>
              </div>
            </div>
          )} */}
        </div>
      </aside>
    </>
  )
}
